import React, { useState, useEffect, useRef } from 'react';
import { getGallery, getGalleryCategories, adminDeleteGalleryItem, adminCreateGalleryCategory, adminDeleteGalleryCategory } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Image, Upload, Loader2, FolderPlus } from 'lucide-react';
import api from '../../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminGallery = () => {
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [uploadCategory, setUploadCategory] = useState('none');
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchData = async () => {
    try {
      const [imgRes, catRes] = await Promise.all([getGallery(), getGalleryCategories()]);
      setImages(imgRes.data);
      setCategories(catRes.data);
    } catch (e) { toast.error('Fehler'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getImageUrl = (img) => {
    const p = img.storage_path || img.image_path;
    if (!p) return null;
    if (p.startsWith('http')) return p;
    return `${API}/api/files/${p}`;
  };

  const doUpload = async (files) => {
    if (!files || files.length === 0) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    for (let f of files) {
      if (!allowed.includes(f.type)) { toast.error(`Ungültiger Typ: ${f.name}`); return; }
      if (f.size > 10 * 1024 * 1024) { toast.error(`Zu groß: ${f.name}`); return; }
    }
    setUploading(true);
    try {
      const formData = new FormData();
      for (let f of files) formData.append('files', f);
      const catParam = uploadCategory && uploadCategory !== 'none' ? `?category_id=${uploadCategory}` : '';
      await api.post(`/admin/gallery/upload${catParam}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${files.length} Bild(er) hochgeladen!`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload fehlgeschlagen'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleFileSelect = (e) => doUpload(e.target.files);
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); };
  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleDelete = async (img) => {
    if (!window.confirm('Bild löschen?')) return;
    try { await adminDeleteGalleryItem(img.image_id); toast.success('Gelöscht'); fetchData(); }
    catch (e) { toast.error('Fehler'); }
  };

  const handleCreateCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await adminCreateGalleryCategory({ name: newCatName.trim() });
      toast.success('Kategorie erstellt');
      setNewCatName('');
      setCatDialogOpen(false);
      fetchData();
    } catch (e) { toast.error('Fehler'); }
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`Kategorie "${cat.name}" löschen?`)) return;
    try { await adminDeleteGalleryCategory(cat.category_id); toast.success('Gelöscht'); fetchData(); }
    catch (e) { toast.error('Fehler'); }
  };

  const filteredImages = selectedCategory === 'all' ? images :
    selectedCategory === 'none' ? images.filter(i => !i.category_id) :
    images.filter(i => i.category_id === selectedCategory);

  return (
    <div data-testid="admin-gallery-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-bold uppercase">Galerie</h1>
        <div className="flex items-center gap-2">
          <Button onClick={() => setCatDialogOpen(true)} variant="outline" className="border-white/20 gap-1">
            <FolderPlus className="w-4 h-4" /> Kategorie
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn-primary" data-testid="upload-gallery-btn">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Hochladen...</> : <><Upload className="w-4 h-4 mr-2" />Bilder hochladen</>}
          </Button>
        </div>
      </div>

      {/* Upload Category + Filter */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <Label className="text-xs text-text-secondary mb-1 block">Upload-Kategorie</Label>
          <Select value={uploadCategory} onValueChange={setUploadCategory}>
            <SelectTrigger className="input-dark"><SelectValue placeholder="Keine Kategorie" /></SelectTrigger>
            <SelectContent className="bg-surface border-white/10">
              <SelectItem value="none">Keine Kategorie</SelectItem>
              {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs text-text-secondary mb-1 block">Filter</Label>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="input-dark"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-white/10">
              <SelectItem value="all">Alle ({images.length})</SelectItem>
              <SelectItem value="none">Ohne Kategorie</SelectItem>
              {categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Categories list */}
      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map(c => (
            <div key={c.category_id} className="flex items-center gap-1 px-3 py-1 card-surface rounded-sm text-sm">
              <span>{c.name}</span>
              <button onClick={() => handleDeleteCategory(c)} className="text-red-400 hover:text-red-300 ml-1"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Drag & Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-sm p-8 text-center mb-6 transition-colors ${
          dragOver ? 'border-accent bg-accent/10' : 'border-white/10'
        }`}
        onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
      >
        <Upload className="w-10 h-10 text-text-secondary mx-auto mb-2" />
        <p className="text-text-secondary text-sm">Bilder hierher ziehen oder</p>
        <Button onClick={() => fileInputRef.current?.click()} variant="link" className="text-accent">Dateien auswählen</Button>
        <p className="text-xs text-text-secondary mt-1">JPG, PNG, WebP (max. 10 MB pro Bild)</p>
      </div>

      {/* Images Grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="aspect-square bg-surface animate-pulse rounded-sm" />)}
        </div>
      ) : filteredImages.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredImages.map(img => {
            const url = getImageUrl(img);
            const cat = categories.find(c => c.category_id === img.category_id);
            return (
              <Card key={img.image_id} className="card-surface overflow-hidden relative group">
                <div className="aspect-square">
                  {url ? <img src={url} alt={img.title} className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full bg-surface-elevated flex items-center justify-center"><Image className="w-10 h-10 text-text-secondary" /></div>}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(img)}><Trash2 className="w-4 h-4 mr-1" />Löschen</Button>
                  </div>
                </div>
                <CardContent className="p-2">
                  <p className="text-xs font-medium truncate">{img.title}</p>
                  {cat && <p className="text-[10px] text-accent">{cat.name}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="card-surface"><CardContent className="p-12 text-center"><Image className="w-16 h-16 text-text-secondary mx-auto mb-4" /><p className="text-text-secondary">Keine Bilder.</p></CardContent></Card>
      )}

      {/* New Category Dialog */}
      <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
        <DialogContent className="bg-surface border-white/10 max-w-sm">
          <DialogHeader><DialogTitle>Neue Kategorie</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input className="input-dark" placeholder="z.B. Liga Spiele, Turniere, Training" value={newCatName} onChange={e => setNewCatName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateCategory()} data-testid="cat-name-input" />
            <Button onClick={handleCreateCategory} className="btn-primary w-full">Erstellen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminGallery;
